class VolumeRewards extends HTMLElement {
  connectedCallback() {
    this.productId     = parseInt(this.dataset.productId, 10);
    this.discountCode  = this.dataset.discountCode;
    this.discountQty   = parseInt(this.dataset.discountQuantity, 10);
    this.defaultQty    = parseInt(this.dataset.defaultQuantity, 10);
    this.form          = document.getElementById(this.dataset.formId);
    this.quantityInput = this.form?.querySelector('[name="quantity"]');

    // The PDP buy_buttons block ships without a quantity_selector — there's no
    // [name="quantity"] input in the form by default. Inject a hidden one so the
    // form submission actually carries our chosen quantity (otherwise Shopify
    // defaults to 1).
    if (!this.quantityInput && this.form) {
      const hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.name = 'quantity';
      hidden.value = '1';
      this.form.appendChild(hidden);
      this.quantityInput = hidden;
    }

    this.cards         = Array.from(this.querySelectorAll('[data-volume-card]'));

    this.cards.forEach((card) => {
      card.addEventListener('click', () => this.selectCard(card));
      card.addEventListener('keydown', (e) => this.handleKeydown(e, card));
    });

    this.onCartChange = () => this.handleCartChange();
    document.documentElement.addEventListener('cart:change', this.onCartChange);

    const currentQty = parseInt(this.quantityInput?.value, 10);
    const matching = Number.isFinite(currentQty)
      ? this.cards.find((c) => parseInt(c.dataset.quantity, 10) === currentQty)
      : null;
    const initial = matching
      || this.cards.find((c) => parseInt(c.dataset.quantity, 10) === this.defaultQty)
      || this.cards[Math.floor(this.cards.length / 2)];

    this.selectCard(initial, { silent: true });
  }

  disconnectedCallback() {
    document.documentElement.removeEventListener('cart:change', this.onCartChange);
  }

  selectCard(card, { silent = false } = {}) {
    if (!card) return;
    this.cards.forEach((c) => {
      const isSelected = c === card;
      c.dataset.selected = isSelected ? 'true' : 'false';
      c.setAttribute('aria-checked', isSelected ? 'true' : 'false');
      c.setAttribute('tabindex', isSelected ? '0' : '-1');
    });
    if (this.quantityInput && this.quantityInput.value !== card.dataset.quantity) {
      this.quantityInput.value = card.dataset.quantity;
      if (!silent) {
        this.quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
        this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    // When the user actively picks the discount tier, attach the code right away
    // (silently — no cart events) so the cart already has it the moment the
    // customer actually clicks Add-to-Cart. The drawer opens on ATC, not before.
    if (!silent) {
      const qty = parseInt(card.dataset.quantity, 10);
      if (Number.isFinite(qty) && this.discountQty && qty >= this.discountQty) {
        this.applyDiscountCode({ notifyCart: false });
      }
    }
  }

  async applyDiscountCode({ notifyCart = true } = {}) {
    if (!this.discountCode) return;

    let cart;
    try {
      cart = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
        headers: { Accept: 'application/json' },
      }).then((r) => r.json());
    } catch (err) {
      return;
    }

    const codes = cart?.discount_codes || [];
    const alreadyApplied = codes.some(
      (d) => d.code?.toUpperCase() === this.discountCode.toUpperCase() && d.applicable,
    );
    const otherCodeApplied = codes.some(
      (d) => d.applicable && d.code?.toUpperCase() !== this.discountCode.toUpperCase(),
    );
    if (alreadyApplied || otherCodeApplied) return;

    try {
      await fetch(`/discount/${encodeURIComponent(this.discountCode)}`, {
        method: 'GET',
        redirect: 'manual',
        credentials: 'same-origin',
      });
    } catch (err) {
      return;
    }

    // Only fire cart events when triggered from an actual cart change (handleCartChange).
    // Skipping these on the silent selection path prevents the cart drawer from
    // auto-opening just because the user picked a tier on the PDP.
    if (notifyCart) {
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true }));
    }
  }

  handleKeydown(e, card) {
    const list = this.cards;
    const idx = list.indexOf(card);
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      this.selectCard(card);
      return;
    }
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    const dir = e.key === 'ArrowRight' ? 1 : -1;
    const next = list[(idx + dir + list.length) % list.length];
    next.focus();
    this.selectCard(next);
  }

  async handleCartChange() {
    // Safety net: if the user reached the discount tier without going through the
    // card UI (e.g. used the standard quantity selector, or +/- inside the cart
    // drawer), still apply the code once the threshold is met in the actual cart.
    if (!this.discountCode || !this.discountQty) return;

    let cart;
    try {
      cart = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
        headers: { Accept: 'application/json' },
      }).then((r) => r.json());
    } catch (err) {
      return;
    }
    if (!cart || !Array.isArray(cart.items)) return;

    const lineQty = cart.items
      .filter((item) => item.product_id === this.productId)
      .reduce((sum, item) => sum + item.quantity, 0);

    if (lineQty >= this.discountQty) {
      this.applyDiscountCode();
    }
  }
}

if (!customElements.get('volume-rewards')) {
  customElements.define('volume-rewards', VolumeRewards);
}
